import React, { useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  useTheme,
  Box,
  IconButton,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import PrintData from "../../../components/common/section/printData";

function ViewSubsectionAccordion({
  fetchContentData,
  subsection,
  section,
  language,
}) {
  const [content, setContent] = React.useState([]);
  const [expandedPanels, setExpandedPanels] = React.useState([]); // Track multiple expanded panels
  const theme = useTheme();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchContentData({
          subsection: subsection.subsection,
          section,
          language,
        });
        setContent(response.data);
      } catch (error) {
        console.error("Error fetching content data:", error);
      }
    };
    fetchData();
  }, [subsection, section, language]);

  const handleToggle = (panel) => () => {
    if (expandedPanels.includes(panel)) {
      setExpandedPanels((prev) => prev.filter((item) => item !== panel)); // Remove panel from expanded list
    } else {
      setExpandedPanels((prev) => [...prev, panel]); // Add panel to expanded list
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "white",
          padding: "10px",
          paddingBottom: "30px",
        }}
      >
        <PrintData fileName={`${subsection.subsection}.pdf`} />
      </Box>
      <Box
        sx={{
          margin: "0 auto",
          padding: theme.spacing(2),
          gap: 2,
          maxWidth: "100vw",
          borderBottom: "2px solid #D3D3D3",
        }}
      >
        <Typography
          variant="h1"
          gutterBottom
          sx={{ fontWeight: "bold", color: theme.palette.text.primary }}
        >
          {subsection.subsection}
        </Typography>
        {content.length > 0 ? (
          content.map((item, index) => (
            <Accordion
              key={index}
              expanded={expandedPanels.includes(index)} // Check if panel is in expanded list
              onChange={handleToggle(index)}
              sx={{
                backgroundColor: expandedPanels.includes(index)
                  ? "#1976d2"
                  : "#f0f0f0", // Blue color for expanded, light grey for collapsed
                padding: 0,
                marginBottom: theme.spacing(2),
                borderRadius: "8px",
                border: "1px solid #E3E3E3",
                transition:
                  "background-color 0.3s ease, box-shadow 0.3s ease, border 0.3s ease",
                "&:before": {
                  display: "none",
                },
              }}
            >
              <AccordionSummary
                expandIcon={
                  expandedPanels.includes(index) ? (
                    <IconButton
                      size="small"
                      sx={{
                        bgcolor: "rgba(238, 192, 68, 1)",
                        color: "#1874D2",
                      }}
                    >
                      <Remove />
                    </IconButton>
                  ) : (
                    <IconButton
                      size="small"
                      sx={{
                        bgcolor: "#1565c0",
                      }}
                    >
                      <Add sx={{ color: "white" }} />
                    </IconButton>
                  )
                }
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
                sx={{
                  borderRadius: "8px",
                  backgroundColor: expandedPanels.includes(index)
                    ? "#1565c0"
                    : "rgba(236, 248, 249, 1)", // Darker blue for expanded, light grey for collapsed
                  color: expandedPanels.includes(index)
                    ? "#ffffff"
                    : theme.palette.text.primary, // White text for expanded, default for collapsed
                  transition: "background-color 0.3s ease, color 0.3s ease",
                  "&.Mui-expanded": {
                    minHeight: "48px",
                  },
                  ".MuiAccordionSummary-content.Mui-expanded": {
                    margin: "12px 0",
                  },
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: expandedPanels.includes(index)
                      ? "#ffffff"
                      : "#878680",
                    fontWeight: "600",
                  }}
                >
                  {item.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  backgroundColor: expandedPanels.includes(index)
                    ? "white"
                    : theme.palette.background.default, // Light blue for expanded
                  padding: theme.spacing(2),
                  borderTop: `1px solid ${theme.palette.divider}`,
                  transition:
                    "background-color 0.3s ease, border-top 0.3s ease",
                  overflowX: "auto",
                  width: "100%",
                }}
              >
                <Typography
                  sx={{ color: theme.palette.text.secondary }}
                  dangerouslySetInnerHTML={{ __html: item.answer }}
                ></Typography>
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          <Typography>Loading...</Typography>
        )}
      </Box>
    </Box>
  );
}

export default ViewSubsectionAccordion;
